import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, ChevronDown, ChevronUp, GitMerge, Loader2, X } from 'lucide-react';
import {
  bulkMergeCustomers,
  previewBulkCustomerMerge,
  type BulkMergeFieldStrategy,
  type BulkMergePreview,
  type BulkMergeSurvivorStrategy,
} from '@/lib/api/customers.js';
import { formatCurrency, formatDate } from '@/lib/format.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { ManagerOverrideModal } from '@/components/common/ManagerOverrideModal.js';
import { useManagerOverride } from '@/hooks/useManagerOverride.js';

export function BulkMergeCustomersModal({
  open,
  customerIds,
  onClose,
  onDone,
}: {
  open: boolean;
  customerIds: string[];
  onClose: () => void;
  onDone: (primaryId: string) => void;
}) {
  const { requireOverride, modalProps: managerOverrideModal } = useManagerOverride();
  const [survivorStrategy, setSurvivorStrategy] =
    useState<BulkMergeSurvivorStrategy>('most_tickets');
  const [manualPrimaryId, setManualPrimaryId] = useState(customerIds[0] ?? '');
  const [fieldStrategy, setFieldStrategy] = useState<BulkMergeFieldStrategy>('fill_blanks');
  const [reason, setReason] = useState('Import duplicates');
  const [reviewOpen, setReviewOpen] = useState(false);
  const [confirmCount, setConfirmCount] = useState('');

  useEffect(() => {
    if (!open) return;
    setSurvivorStrategy('most_tickets');
    setManualPrimaryId(customerIds[0] ?? '');
    setFieldStrategy('fill_blanks');
    setReason('Import duplicates');
    setReviewOpen(false);
    setConfirmCount('');
  }, [open, customerIds]);

  const primaryIdForPreview =
    survivorStrategy === 'manual' ? manualPrimaryId || customerIds[0]! : customerIds[0]!;

  const previewQuery = useQuery({
    queryKey: [
      'bulk-merge-preview',
      customerIds.join(','),
      survivorStrategy,
      fieldStrategy,
      primaryIdForPreview,
    ],
    queryFn: () =>
      previewBulkCustomerMerge({
        customerIds,
        primaryId: primaryIdForPreview,
        survivorStrategy,
        fieldStrategy,
      }),
    enabled: open && customerIds.length >= 2,
  });

  const preview = previewQuery.data;

  useEffect(() => {
    if (preview?.resolvedPrimaryId && survivorStrategy !== 'manual') {
      setManualPrimaryId(preview.resolvedPrimaryId);
    }
  }, [preview?.resolvedPrimaryId, survivorStrategy]);

  const highWarnings = useMemo(
    () => (preview?.warnings ?? []).filter((w) => w.level === 'high'),
    [preview],
  );
  const needsTypedConfirm = highWarnings.length > 0;
  const confirmOk =
    !needsTypedConfirm || confirmCount.trim() === String(customerIds.length);

  const mut = useMutation({
    mutationFn: (managerOverrideAuthorizedBy: string) =>
      bulkMergeCustomers({
        customerIds,
        primaryId:
          survivorStrategy === 'manual'
            ? manualPrimaryId
            : (preview?.resolvedPrimaryId ?? primaryIdForPreview),
        survivorStrategy,
        fieldStrategy,
        reason: reason.trim(),
        managerOverrideAuthorizedBy,
      }),
    onSuccess: (data) => {
      toastSuccess(`Merged ${data.absorbing + 1} customers into one`);
      onDone(data.primaryId);
      onClose();
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  if (!open) return null;

  const blocked = (preview?.openTicketBlockers.length ?? 0) > 0;
  const candidateRows = preview?.customers ?? [];

  async function handleMerge() {
    if (!preview || blocked || !reason.trim() || !confirmOk) return;
    const result = await requireOverride({
      action: 'customer.bulk_merge',
      title: 'Manager Approval to Merge Customers',
      description: `Merging ${customerIds.length} customer records into one`,
      requiredRole: 'store_manager',
      context: { reason: reason.trim() },
    });
    if (!result.approved) return;
    mut.mutate(result.authorizedBy.id);
  }

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
        <div
          className="flex max-h-[92vh] w-full max-w-xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
            <h2 className="text-lg font-semibold text-slate-900">
              Merge {customerIds.length} Customers
            </h2>
            <button
              type="button"
              className="rounded p-1 text-slate-400 hover:bg-slate-100"
              onClick={onClose}
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
            {previewQuery.isLoading ? (
              <p className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                Analyzing selection…
              </p>
            ) : null}

            {previewQuery.isError ? (
              <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                {parseApiError(previewQuery.error).message}
              </p>
            ) : null}

            {preview ? (
              <>
                {highWarnings.map((w) => (
                  <div
                    key={w.message}
                    className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950"
                  >
                    <p className="flex items-start gap-2 font-medium">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                      {w.message}
                    </p>
                    <p className="mt-1 text-amber-800">
                      Verify they are the same person before merging. Shared phone numbers are
                      common between family members.
                    </p>
                  </div>
                ))}

                {preview.warnings
                  .filter((w) => w.level === 'medium')
                  .map((w) => (
                    <p
                      key={w.message}
                      className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                    >
                      {w.message}
                    </p>
                  ))}

                {blocked ? (
                  <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
                    <p className="font-semibold">Cannot merge — open tickets on non-primary</p>
                    <ul className="mt-2 space-y-1">
                      {preview.openTicketBlockers.map((b) => (
                        <li key={b.customerId}>
                          {b.name}:{' '}
                          {b.tickets.map((t) => `${t.ticketNumber} (${t.status})`).join(', ')}
                        </li>
                      ))}
                    </ul>
                    <p className="mt-1">Close or reassign these first.</p>
                  </div>
                ) : null}

                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-sm font-semibold text-primary"
                  onClick={() => setReviewOpen((o) => !o)}
                >
                  Review {reviewOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>
                {reviewOpen ? (
                  <div className="max-h-48 overflow-auto rounded-lg border border-slate-200">
                    <table className="min-w-full text-left text-xs">
                      <thead className="sticky top-0 bg-slate-50 text-slate-500">
                        <tr>
                          <th className="px-2 py-1.5">Name</th>
                          <th className="px-2 py-1.5">Email</th>
                          <th className="px-2 py-1.5">Phone</th>
                          <th className="px-2 py-1.5">Tickets</th>
                          <th className="px-2 py-1.5">Created</th>
                          <th className="px-2 py-1.5">Source</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {preview.customers.map((c) => (
                          <tr key={c.id} className="text-slate-700">
                            <td className="px-2 py-1.5 font-medium">{c.name}</td>
                            <td className="px-2 py-1.5">{c.email ?? '—'}</td>
                            <td className="px-2 py-1.5">{c.phone ?? '—'}</td>
                            <td className="px-2 py-1.5">{c.tickets}</td>
                            <td className="px-2 py-1.5">{formatDate(c.createdAt)}</td>
                            <td className="px-2 py-1.5">{c.importedFrom ?? '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : null}

                <fieldset className="space-y-2">
                  <legend className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Keep as primary
                  </legend>
                  {(
                    [
                      ['most_tickets', 'Most tickets'],
                      ['oldest', 'Oldest record'],
                      ['most_complete', 'Most complete'],
                      ['manual', 'Choose manually'],
                    ] as const
                  ).map(([value, label]) => {
                    const pick =
                      value === 'manual'
                        ? candidateRows.find((c) => c.id === manualPrimaryId)
                        : value === survivorStrategy
                          ? candidateRows.find((c) => c.id === preview.resolvedPrimaryId)
                          : candidateRows[0];
                    const hint =
                      value === 'most_tickets'
                        ? pick
                          ? `${pick.name} · ${pick.tickets} tickets`
                          : ''
                        : value === 'oldest'
                          ? pick
                            ? `${pick.name} · created ${formatDate(pick.createdAt)}`
                            : ''
                          : value === 'most_complete'
                            ? pick
                              ? `${pick.name}`
                              : ''
                            : '';
                    return (
                      <label
                        key={value}
                        className="flex cursor-pointer items-start gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm"
                      >
                        <input
                          type="radio"
                          name="survivor"
                          checked={survivorStrategy === value}
                          onChange={() => setSurvivorStrategy(value)}
                          className="mt-1"
                        />
                        <span>
                          <span className="font-semibold text-slate-900">{label}</span>
                          {hint && value !== 'manual' ? (
                            <span className="mt-0.5 block text-slate-500">{hint}</span>
                          ) : null}
                          {value === 'manual' && survivorStrategy === 'manual' ? (
                            <select
                              className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-sm"
                              value={manualPrimaryId}
                              onChange={(e) => setManualPrimaryId(e.target.value)}
                            >
                              {candidateRows.map((c) => (
                                <option key={c.id} value={c.id}>
                                  {c.name} · {c.tickets} tickets · {formatDate(c.createdAt)}
                                </option>
                              ))}
                            </select>
                          ) : null}
                        </span>
                      </label>
                    );
                  })}
                </fieldset>

                <fieldset className="space-y-2">
                  <legend className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Other fields
                  </legend>
                  <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm">
                    <input
                      type="radio"
                      name="fields"
                      checked={fieldStrategy === 'fill_blanks'}
                      onChange={() => setFieldStrategy('fill_blanks')}
                      className="mt-1"
                    />
                    <span>
                      <span className="font-semibold">Fill blanks from duplicates</span>
                      <span className="mt-0.5 block text-slate-500">
                        Keeps primary values, fills empty fields
                      </span>
                    </span>
                  </label>
                  <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm">
                    <input
                      type="radio"
                      name="fields"
                      checked={fieldStrategy === 'primary_wins'}
                      onChange={() => setFieldStrategy('primary_wins')}
                      className="mt-1"
                    />
                    <span>
                      <span className="font-semibold">Primary only — discard other values</span>
                    </span>
                  </label>
                </fieldset>

                <ResultPreview preview={preview} count={customerIds.length} />

                <label className="block text-sm">
                  <span className="text-slate-600">Reason</span>
                  <input
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                </label>

                {needsTypedConfirm ? (
                  <label className="block text-sm">
                    <span className="text-slate-600">
                      Type {customerIds.length} to confirm
                    </span>
                    <input
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                      value={confirmCount}
                      onChange={(e) => setConfirmCount(e.target.value)}
                      placeholder={String(customerIds.length)}
                    />
                  </label>
                ) : null}
              </>
            ) : null}
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-4">
            <button
              type="button"
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700"
              onClick={onClose}
              disabled={mut.isPending}
            >
              Cancel
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
              disabled={
                !preview ||
                blocked ||
                !reason.trim() ||
                !confirmOk ||
                mut.isPending ||
                previewQuery.isFetching
              }
              onClick={() => void handleMerge()}
            >
              {mut.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Merging {customerIds.length}…
                </>
              ) : (
                <>
                  <GitMerge className="h-4 w-4" />
                  Merge {customerIds.length} Customers →
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      {managerOverrideModal ? <ManagerOverrideModal {...managerOverrideModal} /> : null}
    </>
  );
}

function ResultPreview({
  preview,
  count,
}: {
  preview: BulkMergePreview;
  count: number;
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Result preview</p>
      <p className="mt-1 text-base font-semibold text-slate-950">{preview.primary.name}</p>
      <p className="text-slate-600">
        {preview.primary.email ?? '—'} · {preview.primary.phone ?? '—'}
      </p>
      <p className="mt-1 text-slate-700">
        {preview.totals.ticketsAfter} tickets · {formatCurrency(preview.totals.spendAfter)} spent ·{' '}
        {preview.totals.loyaltyAfter} loyalty pts
      </p>
      <p className="mt-1 text-slate-500">
        {count - 1} record{count - 1 === 1 ? '' : 's'} will be removed
      </p>
    </div>
  );
}
